/**
 * SiblingCloseModal — after approved_lease_started, prompt to close sibling leads.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { decideLead } from '../../../../lib/leasing.api';

export interface SiblingCloseModalProps {
  leadId: string;
  siblingCount: number;
  siblingIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export function SiblingCloseModal({ leadId, siblingCount, siblingIds, onClose, onSuccess }: SiblingCloseModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCloseAll = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      // Re-submit decide with closeSiblings: 'all' to trigger the sibling close
      await decideLead(leadId, {
        decision: 'approved_lease_started',
        closeSiblings: 'all',
        siblingIds,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close sibling leads');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="sibling-close-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sibling-close-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border w-full max-w-md p-6 shadow-lg">
        <h2 id="sibling-close-modal-title" className="text-lg font-semibold mb-2">
          Close Other Leads?
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          There {siblingCount === 1 ? 'is' : 'are'}{' '}
          <strong>{siblingCount} other open lead{siblingCount === 1 ? '' : 's'}</strong> on this
          listing. Would you like to close {siblingCount === 1 ? 'it' : 'them'} as{' '}
          <em>withdrawn — listing filled</em>?
        </p>

        {error && (
          <div
            data-testid="sibling-close-error"
            className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            data-testid="sibling-close-all"
            disabled={isSubmitting}
            onClick={() => void handleCloseAll()}
            className="w-full"
          >
            {isSubmitting ? 'Closing…' : `Close all ${siblingCount === 1 ? 'lead' : `${siblingCount} leads`} as withdrawn`}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="sibling-keep-open"
            disabled={isSubmitting}
            onClick={() => { onSuccess(); onClose(); }}
            className="w-full"
          >
            Keep all open
          </Button>
        </div>
      </div>
    </div>
  );
}
